import { Avatar, Divider, IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material"
import { IconAddProject } from "../../../icons/IconAddProject";
import { IconAddSocialOrder } from "../../../icons/IconAddSocialOrder";
import { IconAddPersonalGrowthTools } from "../../../icons/IconAddPersonalGrowthTools";
import { IconAddTeams } from "../../../icons/IconAddTeams";
import { IconSettings } from "../../../icons/IconSettings";
import { ExitToAppRoundedIcon } from "../../../icons/ExitToAppRoundedIcon";
import { useState } from "react";
import { AvatarButtonStyled, AvatarStyled, MenuListStyled, MenuPaperStyled } from "./style";
import { useNavigate } from "react-router-dom";
import { routesProfile } from "../../../consts";
import { IconProfile } from "../../../icons/IconProfile";
import { revokeToken } from "../../../utils/api";
import { cleanupWebPushSubscription } from "../../../utils/webPush";
import { useAppDispatch } from "../../../hooks";
import { routesGuest } from "../../../consts";
import {
  dictionariesApi,
  growthToolsApi,
  notificationsApi,
  platformReportsApi,
  profileApi,
  projectsApi,
  socialOrdersApi,
  teamsApi,
  usersApi,
} from "../../../api";

interface UserAccountMenuProps {
  isTablet: boolean;
}

export const UserAccountMenu = ({isTablet}: UserAccountMenuProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { data: me } = profileApi.useGetProfileQuery()
    const avatarSrc = me?.avatar ?? undefined

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleNavigate = (path: string) => {
        handleMenuClose();
        navigate(path);
    };

    const handleLogout = async () => {
        handleMenuClose();
        try {
            await cleanupWebPushSubscription();
        } catch {
            // best effort
        }
        await revokeToken();

        // Ensure auth-dependent UI resets immediately.
        dispatch(profileApi.util.resetApiState());
        dispatch(dictionariesApi.util.resetApiState());
        dispatch(usersApi.util.resetApiState());
        dispatch(projectsApi.util.resetApiState());
        dispatch(teamsApi.util.resetApiState());
        dispatch(growthToolsApi.util.resetApiState());
        dispatch(socialOrdersApi.util.resetApiState());
        dispatch(notificationsApi.util.resetApiState());
        dispatch(platformReportsApi.util.resetApiState());

        navigate(routesGuest.ROOT.path, { replace: true });
    };

    return(
        <>
            <IconButton
                onClick={handleMenuOpen}
                disableRipple
                disableFocusRipple
                sx={AvatarButtonStyled}
            >
                <Avatar
                    alt="Avatar"
                    src={avatarSrc}
                    sx={AvatarStyled}
                />
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: MenuPaperStyled,
                    },
                    list: {
                        disablePadding: true,
                        sx: MenuListStyled,
                    }
                }}
            >
                <MenuItem 
                    disableRipple 
                    onClick={() => handleNavigate(`/${routesProfile.PROFILE.path}`)}
                >
                    <Stack 
                        direction="row" 
                        alignItems="center"
                        height="45px"
                        minWidth={0}
                        gap={1} 
                    >
                        <Avatar 
                            alt="Avatar"
                            src={avatarSrc} 
                        />
                        <Stack 
                            direction="column" 
                            height="100%" 
                            justifyContent="space-between"
                            sx={{ overflow: 'hidden' }}
                        >
                            <Typography variant="black-14-medium" noWrap>{me?.shortname}</Typography>
                            <Typography variant="black-14-normal" noWrap>{me?.slug}</Typography>
                        </Stack>
                    </Stack>
                </MenuItem>
                {isTablet && (
                    <>
                        <Divider />
                        <MenuItem
                            onClick={() => handleNavigate(`${routesProfile.USERS.path}/${me?.slug}`)}
                        >
                            <IconProfile fontSize="medium" />
                            Ваша страница
                        </MenuItem>
                        <MenuItem
                            onClick={() => handleNavigate(`${routesProfile.PROJECTS.path}/create`)}
                        >
                            <IconAddProject fontSize="medium" />
                            Новый проект
                        </MenuItem>
                        <MenuItem
                            onClick={() => handleNavigate(`/${routesProfile.SOCIALORDERS.path}/create`)}
                        >
                            <IconAddSocialOrder fontSize="medium" />
                            Новый соц-заказ
                        </MenuItem>
                        <MenuItem
                            onClick={() => handleNavigate(`/${routesProfile.GROWTHTOOLS.path}/create`)}
                        >
                            <IconAddPersonalGrowthTools fontSize="medium" />
                            Новый инструмент роста
                        </MenuItem>
                        <MenuItem
                            onClick={() => handleNavigate(`/${routesProfile.TEAMS.path}/create`)}
                        >
                            <IconAddTeams fontSize="medium" />
                            Новая команда
                        </MenuItem>
                        <MenuItem
                            onClick={() => handleNavigate(`/${routesProfile.SETTINGS.path}`)}
                        >
                            <IconSettings fontSize="medium" />
                            Настройки
                        </MenuItem>
                    </>
                )}
                {me?.is_platform_staff === true ? (
                  <>
                    <Divider />
                    <MenuItem
                      onClick={() => handleNavigate(`/${routesProfile.STAFF_REPORTS.path}`)}
                    >
                      <IconSettings fontSize="medium" />
                      Жалобы платформы
                    </MenuItem>
                  </>
                ) : null}
                <Divider />
                <MenuItem 
                    onClick={handleLogout} 
                >
                    <ExitToAppRoundedIcon fontSize="medium" />
                    Выйти
                </MenuItem>
            </Menu>
        </>
    )
}
